import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';

const SERVICES = [
  {
    icon: 'ri-robot-2-line',
    title: 'Agenci AI na stronę',
    desc: 'Inteligentni asystenci obsługujący klientów 24/7. Odpowiadają na pytania, kwalifikują leady i umawiają spotkania — bez udziału człowieka.',
    color: '#00d4ff',
    link: '/uslugi/agenci-ai',
    tags: ['Chatbot AI', 'Automatyzacja', 'Lead Kwalifikacja'],
    img: 'https://public.readdy.ai/ai/img_res/88677969c60526ee1bca838949190789.jpg',
  },
  {
    icon: 'ri-leaf-line',
    title: 'Projekty EUDR z AI',
    desc: 'Compliance z unijnym rozporządzeniem EUDR wspomagany sztuczną inteligencją. Automatyczna analiza łańcucha dostaw, dokumentacja i raportowanie.',
    color: '#00ff88',
    link: '/uslugi/eudr-ai',
    tags: ['EUDR', 'Compliance', 'Analiza danych'],
    img: 'https://readdy.ai/api/search-image?query=EU%20regulation%20compliance%20data%20analysis%20AI%20system%20forest%20supply%20chain%20deforestation%20tracking%20digital%20dashboard%20green%20technology%20environmental%20compliance%20monitoring%20platform%20dark%20background&width=600&height=400&seq=aisrv2&orientation=landscape',
  },
  {
    icon: 'ri-graduation-cap-line',
    title: 'Szkolenia z AI',
    desc: 'Praktyczne warsztaty i szkolenia dla firm — jak wdrożyć AI w codziennej pracy, automatyzować procesy i zwiększyć efektywność zespołu.',
    color: '#ff6b9d',
    link: '/uslugi/szkolenia-ai',
    tags: ['Warsztaty', 'Wdrożenie', 'Zespoły'],
    img: 'https://readdy.ai/api/search-image?query=AI%20training%20workshop%20professional%20team%20learning%20artificial%20intelligence%20technology%20dark%20modern%20conference%20room%20presentation%20screen%20neural%20network%20diagram%20corporate%20education%20premium&width=600&height=400&seq=aisrv3&orientation=landscape',
  },
  {
    icon: 'ri-code-box-line',
    title: 'Oprogramowanie AI na miarę',
    desc: 'Specjalistyczne programy AI dedykowane konkretnym producentom i branżom. Od analizy danych po inteligentne systemy decyzyjne.',
    color: '#00d4ff',
    link: '/uslugi/oprogramowanie-ai',
    tags: ['Custom AI', 'Integracje', 'Analityka'],
    img: 'https://readdy.ai/api/search-image?query=custom%20AI%20software%20development%20code%20interface%20dark%20background%20glowing%20tech%20futuristic%20programming%20environment%20machine%20learning%20model%20training%20data%20visualization%20modern%20premium%20studio&width=600&height=400&seq=aisrv4&orientation=landscape',
  },
  {
    icon: 'ri-window-line',
    title: 'Strony WWW Premium',
    desc: 'Projektujemy i budujemy strony, które sprzedają. Produktowy design, szybkie ładowanie, perfekcyjna konwersja — z wbudowanym AI.',
    color: '#00ff88',
    link: '/uslugi/strony-www-premium',
    tags: ['UI/UX', 'Performance', 'CRO'],
    img: 'https://readdy.ai/api/search-image?query=ultra%20modern%20premium%20website%20design%20displayed%20on%20sleek%20laptop%20dark%20background%20glowing%20interface%20neon%20cyan%20accents%20minimalist%20hi-tech%20web%20design%20showcase%20professional%20digital%20product%20AI%20integrated&width=600&height=400&seq=aisrv5&orientation=landscape',
  },
  {
    icon: 'ri-line-chart-line',
    title: 'SEO & Lokalne pozycjonowanie',
    desc: 'Widoczność w Google, która przekłada się na leady. Lokalne SEO, Google Wizytówka i pozycjonowanie treści oparte na AI i danych.',
    color: '#ff6b9d',
    link: '/uslugi/seo-pozycjonowanie',
    tags: ['Local SEO', 'Google Maps', 'Content AI'],
    img: 'https://readdy.ai/api/search-image?query=SEO%20analytics%20AI%20powered%20dashboard%20dark%20theme%20glowing%20charts%20search%20engine%20optimization%20artificial%20intelligence%20data%20driven%20digital%20marketing%20local%20search%20modern%20interface&width=600&height=400&seq=aisrv6&orientation=landscape',
  },
];

function ServiceCard({ service, index }: { service: typeof SERVICES[0]; index: number }) {
  const [hovered, setHovered] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="group relative rounded-2xl overflow-hidden border border-white/10 bg-[#0d0d0d] cursor-pointer transition-all duration-500"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(40px)',
        transition: `opacity 0.6s ease ${index * 0.1}s, transform 0.6s ease ${index * 0.1}s, box-shadow 0.3s ease, border-color 0.3s ease`,
        boxShadow: hovered ? `0 20px 60px ${service.color}20` : 'none',
        borderColor: hovered ? `${service.color}40` : 'rgba(255,255,255,0.1)',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Image */}
      <div className="relative h-48 overflow-hidden">
        <img
          src={service.img}
          alt={service.title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] via-[#0d0d0d]/40 to-transparent" />
        <div
          className="absolute top-4 left-4 w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ background: `${service.color}20`, border: `1px solid ${service.color}40` }}
        >
          <i className={`${service.icon} text-xl`} style={{ color: service.color }} />
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <h3 className="text-lg font-bold text-white mb-2">{service.title}</h3>
        <p className="text-white/50 text-sm leading-relaxed mb-4">{service.desc}</p>

        <div className="flex flex-wrap gap-2 mb-5">
          {service.tags.map((tag) => (
            <span
              key={tag}
              className="px-2.5 py-1 rounded-full text-xs font-medium"
              style={{ background: `${service.color}15`, color: service.color, border: `1px solid ${service.color}30` }}
            >
              {tag}
            </span>
          ))}
        </div>

        <Link
          to={service.link}
          className="inline-flex items-center gap-2 text-sm font-semibold transition-all duration-300 group-hover:gap-3"
          style={{ color: service.color }}
        >
          Dowiedz się więcej
          <i className="ri-arrow-right-line" />
        </Link>
      </div>

      <div
        className="absolute bottom-0 left-0 right-0 h-0.5 transition-opacity duration-500"
        style={{
          background: `linear-gradient(90deg, transparent, ${service.color}, transparent)`,
          opacity: hovered ? 1 : 0,
        }}
      />
    </div>
  );
}

export default function ServicesSection() {
  return (
    <section className="py-32 bg-[#060606] relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

      <div className="max-w-[1400px] mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
          <div>
            <p className="text-[#00d4ff] text-xs uppercase tracking-[3px] font-semibold mb-4">Nasze usługi AI</p>
            <h2 className="text-4xl md:text-5xl font-black text-white leading-tight">
              Wdrożenia AI, które<br />
              <span className="text-white/40">realnie działają w Twojej firmie</span>
            </h2>
          </div>
          <Link
            to="/uslugi"
            className="flex-shrink-0 inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-white/20 text-white/70 hover:text-white hover:border-white/40 transition-all text-sm font-medium whitespace-nowrap cursor-pointer"
          >
            Wszystkie usługi
            <i className="ri-arrow-right-line" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {SERVICES.map((s, i) => (
            <ServiceCard key={s.title} service={s} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
